import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { map, catchError } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import {
    IOrganisationResponse,
    IOrganisation,
    IAggregatedClientResponse,
    IAggregatedClient,
    IAggregatedOrganisationDetails,
    IAggregatedAddress,
    IAggregatedFeeDetail,
    IAggregatedAdditionalDetail,
    IAggregatedContactDetail,
    IAggregatedCurriculumDetail,
    IClientDataOrganisation,
    IFeeDetail,
    IContactDetail,
    ICurriculumDetail,
    IAdditionalDetail,
    IOrganisationRequestMetadata,
    isCurriculumStatusActiveForUi,
} from '../models/client.model'
import { ErrorHandlerHelper } from '@app/core/helpers/error-handler.helper'
import { UserStore } from '@core/services/user/user.store'
import { getUserId } from '@app/core/helpers/user.helper'
import {
    AGGREGATED_CLIENTS_CONSTANTS,
    ADDRESS_TYPE_VALUES,
} from '../constants/aggregated-clients.constants'
import type { TViewType } from '@app/core/models/ruc.model'

@Injectable({
    providedIn: 'root',
})
export class AggregatedClientsService {
    private readonly http = inject(HttpClient)
    private readonly baseUrl = environment.apiUrl
    private readonly userStore = inject(UserStore)

    /**
     * Fetches clients from the aggregated clients API
     * @param orgCodeOrCodes Optional organisation code(s) to filter by
     * @param page Page number (default: 1)
     * @param limit Number of items per page (default: 10)
     * @returns Observable of IOrganisationResponse
     */
    getClients(
        orgCodeOrCodes?: string | string[],
        page: number = AGGREGATED_CLIENTS_CONSTANTS.PAGINATION.DEFAULT_PAGE,
        limit: number = AGGREGATED_CLIENTS_CONSTANTS.PAGINATION.DEFAULT_LIMIT,
        clientIds?: number[],
        search?: string,
        view?: TViewType,
    ): Observable<IOrganisationResponse> {
        const userId = getUserId(this.userStore)
        if (!userId) {
            return throwError(
                () => new Error(AGGREGATED_CLIENTS_CONSTANTS.ERROR_MESSAGES.USER_ID_NOT_AVAILABLE),
            )
        }

        return this.getClientsForUser(userId, orgCodeOrCodes, page, limit, clientIds, search, view)
    }

    getClientsForUser(
        userId: string,
        orgCodeOrCodes?: string | string[],
        page: number = AGGREGATED_CLIENTS_CONSTANTS.PAGINATION.DEFAULT_PAGE,
        limit: number = AGGREGATED_CLIENTS_CONSTANTS.PAGINATION.DEFAULT_LIMIT,
        clientIds?: number[],
        search?: string,
        view?: TViewType,
    ): Observable<IOrganisationResponse> {
        const normalizedUserId = userId?.trim()
        if (!normalizedUserId) {
            return throwError(
                () => new Error(AGGREGATED_CLIENTS_CONSTANTS.ERROR_MESSAGES.USER_ID_NOT_AVAILABLE),
            )
        }

        const normalizedSearch = (search || '').trim()
        const params = this.buildQueryParams(
            this.normalizeOrganisationCodes(orgCodeOrCodes) || undefined,
            page,
            limit,
            clientIds,
            normalizedSearch || undefined,
            view,
        )
        const endpoint = AGGREGATED_CLIENTS_CONSTANTS.API.getClientsEndpoint(normalizedUserId)

        return this.http
            .get<IAggregatedClientResponse>(`${this.baseUrl}${endpoint}`, { params })
            .pipe(
                map((response) => this.transformAggregatedResponseToOrganisationResponse(response)),
                catchError((error: HttpErrorResponse) => this.handleError(error)),
            )
    }

    /**
     * Builds query parameters for the API request.
     * Uses `organisation_codes` (comma-separated list) as expected by
     * `/api/aggregated/v1/users/{user_id}/clients`.
     */
    private buildQueryParams(
        orgCode: string | undefined,
        page: number,
        limit: number,
        clientIds?: number[],
        search?: string,
        view?: TViewType,
    ): HttpParams {
        let params = new HttpParams()
            .set(AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.PAGE, page.toString())
            .set(AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.LIMIT, limit.toString())

        if (search) {
            params = params.set(AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.SEARCH, search)
        }

        if (view) {
            params = params.set(AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.VIEW, view)
        }

        if (orgCode) {
            params = params.set(
                AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.ORGANISATION_CODES,
                orgCode,
            )
        }

        if (clientIds?.length) {
            params = params.set(
                AGGREGATED_CLIENTS_CONSTANTS.QUERY_PARAMS.ORGANISATION_CODES,
                clientIds.join(','),
            )
        }

        return params
    }

    /**
     * Normalizes organisation code input into a comma-separated list.
     * Supports both string arrays and comma-separated string input.
     */
    private normalizeOrganisationCodes(orgCodeOrCodes: string | string[] | undefined): string {
        if (!orgCodeOrCodes) {
            return ''
        }

        const values = Array.isArray(orgCodeOrCodes) ? orgCodeOrCodes : orgCodeOrCodes.split(',')

        return values
            .map((code) => code.trim())
            .filter((code) => code?.length)
            .join(',')
    }

    /**
     * Transforms the aggregated clients API response to IOrganisationResponse format
     */
    private transformAggregatedResponseToOrganisationResponse(
        response: IAggregatedClientResponse,
    ): IOrganisationResponse {
        const organisations = response.data
            .map((client: IAggregatedClient) => this.extractOrganisationFromClient(client))
            .filter(
                (
                    org:
                        | (IOrganisation & { _requestMetadata?: IOrganisationRequestMetadata })
                        | null,
                ): org is IOrganisation & { _requestMetadata?: IOrganisationRequestMetadata } =>
                    org !== null,
            )

        return {
            success: response.success,
            message: response.message,
            data: organisations,
            pagination: {
                page: response.pagination.page,
                limit: response.pagination.limit,
                total: response.pagination.total,
                ...(response.pagination.total_pages !== undefined && {
                    total_pages: response.pagination.total_pages,
                }),
            },
        }
    }

    /**
     * Extracts organisation details from client data
     * Handles both organisation_details and client_data.organisation structures
     */
    private extractOrganisationFromClient(
        client: IAggregatedClient,
    ): (IOrganisation & { _requestMetadata?: IOrganisationRequestMetadata }) | null {
        const orgDetails = client.organisation_details || this.extractFromClientData(client)

        if (!orgDetails) {
            return null
        }

        const organisation = this.transformAggregatedOrganisationToOrganisation(orgDetails)

        // Attach metadata from aggregated client (engagement, request info etc.)
        const metadata: IOrganisationRequestMetadata = {
            // Aggregated client level metadata
            engagementTypeName: client.engagement_type,
            customerId: client.customer_id,
            leadIds: client.lead_ids,
            territoryId: client.territory_id,
            territoryName: client.territory_name,
        }

        // Request-level metadata (for client_data responses)
        if (client.client_request_id !== undefined) {
            Object.assign(metadata, {
                clientRequestId: client.client_request_id,
                requestType: client.request_type,
                requestStatus: client.request_status,
                requestedBy: client.requested_by,
                requestedByUsername: client.requested_by_username,
                reviewedBy: client.reviewed_by,
                reviewedAt: client.reviewed_at,
                requestedAt: client.requested_at,
                remark: client.remark,
                hasDuplicates: client.has_duplicates,
                duplicates: client.duplicates,
            })
        }

        // Only attach metadata if we have at least one defined field
        const hasAnyMetadataField = Object.values(metadata).some(
            (value) => value !== undefined && value !== null,
        )

        if (hasAnyMetadataField) {
            ;(
                organisation as IOrganisation & {
                    _requestMetadata?: IOrganisationRequestMetadata
                }
            )._requestMetadata = metadata
        }

        return organisation
    }

    /**
     * Extracts organisation details from client_data structure
     */
    private extractFromClientData(
        client: IAggregatedClient,
    ): IAggregatedOrganisationDetails | IClientDataOrganisation | null {
        if (!client.client_data?.organisation) {
            return null
        }

        return client.client_data.organisation
    }

    /**
     * Transforms aggregated organisation details to IOrganisation format
     */
    private transformAggregatedOrganisationToOrganisation(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): IOrganisation {
        const primaryAddress = this.findPrimaryAddress(orgDetails)
        const transformedFeeDetails = this.transformFeeDetails(orgDetails)
        const transformedAdditionalDetails = this.transformAdditionalDetails(orgDetails)
        const transformedCurriculumDetails = this.transformCurriculumDetails(orgDetails)
        const transformedContactDetails = this.transformContactDetails(orgDetails)
        const organisationCode = this.extractOrganisationCode(orgDetails)

        return {
            organisation_code: organisationCode,
            organisation_name: orgDetails.organisation_name || '',
            organisation_type: orgDetails.organisation_type || '',
            vertical_type: orgDetails.vertical_type || '',
            website: this.normalizeWebsite(orgDetails.website),
            address_line: primaryAddress?.address_line1 || orgDetails.address_line || undefined,
            address_line2: primaryAddress?.address_line2 ?? orgDetails.address_line2 ?? undefined,
            landmark: primaryAddress?.landmark || orgDetails.landmark || undefined,
            location: primaryAddress?.location_url || orgDetails.location || undefined,
            longitude: primaryAddress?.longitude ?? orgDetails.longitude ?? undefined,
            latitude: primaryAddress?.latitude ?? orgDetails.latitude ?? undefined,
            ward:
                primaryAddress?.ward ||
                (this.hasWardId(orgDetails) ? String(orgDetails.ward_id) : undefined) ||
                undefined,
            postal_code: primaryAddress?.postal_code || orgDetails.postal_code || undefined,
            organisation_status: this.extractOrganisationStatus(orgDetails),
            organisation_deleted_at: this.extractOrganisationDeletedAt(orgDetails),
            organisation_created_by: this.extractOrganisationCreatedBy(orgDetails),
            organisation_updated_by: this.extractOrganisationUpdatedBy(orgDetails),
            organisation_created_at: this.extractOrganisationCreatedAt(orgDetails),
            organisation_updated_at: this.extractOrganisationUpdatedAt(orgDetails),
            contact_details: transformedContactDetails,
            curriculum_details: transformedCurriculumDetails,
            fee_details: transformedFeeDetails,
            group_details: this.extractGroupDetails(orgDetails),
            tax_details: this.extractTaxDetails(orgDetails),
            additional_details: transformedAdditionalDetails,
            area_name: primaryAddress?.area || orgDetails.area_name || undefined,
            city: primaryAddress?.city || orgDetails.city || undefined,
            district: primaryAddress?.district || orgDetails.district || undefined,
            state: primaryAddress?.state || orgDetails.state || undefined,
            country: primaryAddress?.country || orgDetails.country || undefined,
        }
    }

    /**
     * Finds the primary address from addresses array
     */
    private findPrimaryAddress(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): IAggregatedAddress | null {
        if (!this.hasAddresses(orgDetails)) {
            return null
        }

        const addresses = orgDetails.addresses || []
        const shippingAddress = addresses.find(
            (addr: IAggregatedAddress) =>
                addr.address_type === ADDRESS_TYPE_VALUES[0] ||
                addr.address_type === ADDRESS_TYPE_VALUES[2],
        )
        const billingAddress = addresses.find(
            (addr: IAggregatedAddress) =>
                addr.address_type === ADDRESS_TYPE_VALUES[1] ||
                addr.address_type === ADDRESS_TYPE_VALUES[2],
        )

        return shippingAddress || billingAddress || addresses[0] || null
    }

    /**
     * Transforms fee details array, converting string values to numbers
     */
    private transformFeeDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): IFeeDetail[] | undefined {
        if (!this.hasFeeDetails(orgDetails)) {
            return undefined
        }

        const feeDetails = orgDetails.fee_details
        if (!feeDetails?.length) {
            return undefined
        }

        return feeDetails.map((fee: IAggregatedFeeDetail) => ({
            fees_details_id: fee.fees_details_id,
            monthly_fees: this.convertFeeToNumber(fee.monthly_fees),
            annual_fee: this.convertFeeToNumber(fee.annual_fee),
            development_fee: this.convertFeeToNumber(fee.development_fee),
            smart_class_fee: this.convertFeeToNumber(fee.smart_class_fee),
            tuition_fee: this.convertFeeToNumber(fee.tuition_fee),
            edtech_fee: this.convertFeeToNumber(fee.edtech_fee),
            technical_fee: this.convertFeeToNumber(fee.technical_fee),
            fee_created_by: fee.fee_created_by || '',
            fee_updated_by: fee.fee_updated_by ?? null,
            fee_created_at: fee.fee_created_at || '',
            fee_updated_at: fee.fee_updated_at || '',
        }))
    }

    /**
     * Transforms additional details array
     */
    private transformAdditionalDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): IAdditionalDetail[] | undefined {
        if (!this.hasAdditionalDetails(orgDetails)) {
            return undefined
        }

        const additionalDetails = orgDetails.additional_details
        if (!additionalDetails?.length) {
            return undefined
        }

        return additionalDetails.map((detail: IAggregatedAdditionalDetail) => {
            const interestedProducts = this.convertInterestedProductsToArray(
                detail.interested_products,
            )

            return {
                additional_id: detail.additional_id,
                organisation_code: detail.organisation_code,
                student_strength: detail.student_strength ?? null,
                teacher_strength: detail.teacher_strength ?? null,
                lowest_class: detail.lowest_class ?? null,
                highest_class: detail.highest_class ?? null,
                board_results: detail.board_results ?? null,
                year_of_establishment: detail.year_of_establishment ?? null,
                interested_products: interestedProducts,
                tech_profile: detail.tech_profile ?? null,
                description: detail.description ?? null,
                additional_details_created_at: detail.created_at || '',
                additional_details_updated_at: detail.updated_at || '',
                additional_details_created_by: detail.created_by || '',
                additional_details_updated_by: detail.updated_by || '',
            }
        })
    }

    /**
     * Converts interested_products from object to array format
     */
    private convertInterestedProductsToArray(
        products: string[] | Record<string, boolean> | null | undefined,
    ): string[] | null {
        if (!products) {
            return null
        }

        if (Array.isArray(products)) {
            return products
        }

        if (typeof products === 'object' && products !== null) {
            const productsObj = products as Record<string, boolean>
            return Object.keys(productsObj).filter((key: string) => productsObj[key] === true)
        }

        return null
    }

    /**
     * Transforms curriculum details array
     */
    private transformCurriculumDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): ICurriculumDetail[] | undefined {
        if (!this.hasCurriculumDetails(orgDetails)) {
            return undefined
        }

        const curriculumDetails = orgDetails.curriculum_details
        if (!curriculumDetails?.length) {
            return undefined
        }

        const active = curriculumDetails
            .filter((curriculum: IAggregatedCurriculumDetail) =>
                isCurriculumStatusActiveForUi(curriculum),
            )
            .map((curriculum: IAggregatedCurriculumDetail) => ({
                ...curriculum,
                curriculum_detail: curriculum.curriculum_detail || {},
            }))
        return active.length ? active : undefined
    }

    /**
     * Transforms contact details array
     */
    private transformContactDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): IContactDetail[] | undefined {
        if (!this.hasContactDetails(orgDetails)) {
            return undefined
        }

        const contactDetails = orgDetails.contact_details
        if (!contactDetails?.length) {
            return undefined
        }

        return contactDetails.map((contact: IAggregatedContactDetail) => ({
            contact_id: contact.contact_id,
            contact_client_type: contact.contact_client_type,
            contact_client_ref_id: contact.contact_client_ref_id,
            contact_designation: contact.contact_designation,
            contact_name: contact.contact_name || '',
            contact_email: contact.contact_email || '',
            contact_email_consent: contact.contact_email_consent ?? false,
            contact_country_code: contact.contact_country_code ?? 0,
            contact_mobile_number: contact.contact_mobile_number || '',
            contact_mobile_consent: contact.contact_mobile_consent ?? false,
            contact_is_poc: contact.contact_is_poc ?? false,
            contact_status: contact.contact_status,
            contact_created_by: contact.contact_created_by || '',
            contact_updated_by: contact.contact_updated_by || '',
            contact_created_at: contact.contact_created_at,
            contact_updated_at: contact.contact_updated_at,
        }))
    }

    /**
     * Converts fee value from string to number
     */
    private convertFeeToNumber(fee: string | number | null | undefined): number {
        if (fee === null || fee === undefined || fee === '') {
            return 0
        }

        if (typeof fee === 'number') {
            return fee
        }

        const parsed = parseFloat(String(fee))
        return isNaN(parsed) ? 0 : parsed
    }

    /**
     * Extracts organisation code from different structures
     */
    private extractOrganisationCode(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): number {
        if (this.isFullOrganisationDetails(orgDetails)) {
            return orgDetails.organisation_code
        }

        return orgDetails.organisation_code || 0
    }

    /**
     * Type guards and extractors for optional fields
     */
    private hasAddresses(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'addresses' in orgDetails &&
            Array.isArray(orgDetails.addresses) &&
            orgDetails.addresses.length > 0
        )
    }

    private hasFeeDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'fee_details' in orgDetails &&
            Array.isArray(orgDetails.fee_details) &&
            orgDetails.fee_details.length > 0
        )
    }

    private hasAdditionalDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'additional_details' in orgDetails &&
            Array.isArray(orgDetails.additional_details) &&
            orgDetails.additional_details.length > 0
        )
    }

    private hasCurriculumDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'curriculum_details' in orgDetails &&
            Array.isArray(orgDetails.curriculum_details) &&
            orgDetails.curriculum_details.length > 0
        )
    }

    private hasContactDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'contact_details' in orgDetails &&
            Array.isArray(orgDetails.contact_details) &&
            orgDetails.contact_details.length > 0
        )
    }

    private hasWardId(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return (
            'ward_id' in orgDetails &&
            orgDetails.ward_id !== null &&
            orgDetails.ward_id !== undefined
        )
    }

    private isFullOrganisationDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): orgDetails is IAggregatedOrganisationDetails {
        return 'organisation_code' in orgDetails && typeof orgDetails.organisation_code === 'number'
    }

    private extractOrganisationStatus(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): boolean | undefined {
        if (this.isFullOrganisationDetails(orgDetails)) {
            return orgDetails.organisation_status ?? orgDetails.status ?? true
        }
        return true
    }

    private extractOrganisationDeletedAt(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): string | null | undefined {
        return this.isFullOrganisationDetails(orgDetails)
            ? orgDetails.organisation_deleted_at
            : undefined
    }

    private extractOrganisationCreatedBy(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): string | null | undefined {
        return this.isFullOrganisationDetails(orgDetails)
            ? orgDetails.organisation_created_by
            : orgDetails.created_by || undefined
    }

    private extractOrganisationUpdatedBy(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): string | undefined {
        return this.isFullOrganisationDetails(orgDetails)
            ? orgDetails.organisation_updated_by || undefined
            : undefined
    }

    private extractOrganisationCreatedAt(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): string | undefined {
        return this.isFullOrganisationDetails(orgDetails)
            ? orgDetails.organisation_created_at
            : undefined
    }

    private extractOrganisationUpdatedAt(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ): string | undefined {
        return this.isFullOrganisationDetails(orgDetails)
            ? orgDetails.organisation_updated_at
            : undefined
    }

    private extractGroupDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ) {
        return this.isFullOrganisationDetails(orgDetails) ? orgDetails.group_details : undefined
    }

    private extractTaxDetails(
        orgDetails: IAggregatedOrganisationDetails | IClientDataOrganisation,
    ) {
        return this.isFullOrganisationDetails(orgDetails) ? orgDetails.tax_details : undefined
    }

    /**
     * Normalizes website value - converts "-" to undefined
     */
    private normalizeWebsite(website: string | null | undefined): string | undefined {
        if (!website || website === '-' || website.trim() === '') {
            return undefined
        }
        return website
    }

    /**
     * Handles HTTP errors
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        const errorMessage = ErrorHandlerHelper.handleHttpError(error, {
            404: AGGREGATED_CLIENTS_CONSTANTS.ERROR_MESSAGES.CLIENT_NOT_FOUND,
            403: AGGREGATED_CLIENTS_CONSTANTS.ERROR_MESSAGES.ACCESS_DENIED,
        })
        return throwError(() => new Error(errorMessage))
    }
}
